/**
 * Which capture on this device is already a scan on the hub (card
 * `t_8ae1d20d`/E2).
 *
 * A capture is uploaded once and then exists in two places that know nothing
 * about each other: the device's `Documents/RoomScan/<id>/` and the hub's
 * `Scan` row. Nothing in either store names the other, so the app keeps the
 * pairing itself — one small record per upload, `captureId → scanId`, written
 * when the hub answers 201 and read by the settings page so a capture it shows
 * is not uploaded twice.
 *
 * Deliberately shaped like `config/roomScan.ts`: pure, with an injectable
 * `Storage`, so a Node test drives every branch without a WebView. A registry
 * that cannot be read is not an error — the worst it can do is offer an upload
 * of something the hub already has, and the hub stores it as a second scan
 * rather than losing anything.
 */

export const SCAN_UPLOAD_STORAGE_KEY = 'scan_uploads_v1';

/** One upload: the device's capture id, the hub's scan id, and enough to label it. */
export interface ScanUploadRecord {
  captureId: string;
  scanId: string;
  /** The name the scan carries on the hub (the panel's label, or the capture id). */
  name: string;
  /** ISO timestamp of the upload — `new Date().toISOString()`. */
  uploadedAt: string;
}

/** The half of `Storage` this module reads and writes. */
export interface ScanUploadStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/** `localStorage` when it exists and is reachable, `null` otherwise. */
export function defaultScanUploadStorage(): ScanUploadStorage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    // A blocked storage (private mode, a policy) throws on access.
    return null;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function toRecord(value: unknown): ScanUploadRecord | null {
  if (!isRecord(value)) return null;
  const { captureId, scanId, name, uploadedAt } = value;
  if (typeof captureId !== 'string' || !captureId) return null;
  if (typeof scanId !== 'string' || !scanId) return null;
  return {
    captureId,
    scanId,
    name: typeof name === 'string' ? name : captureId,
    uploadedAt: typeof uploadedAt === 'string' ? uploadedAt : '',
  };
}

/**
 * Every upload this device remembers, newest first. A malformed entry is
 * dropped, not thrown over: the registry is a convenience, and a half-written
 * record must not break the page that reads it.
 */
export function readScanUploads(storage: ScanUploadStorage | null = defaultScanUploadStorage()): ScanUploadRecord[] {
  if (!storage) return [];
  try {
    const raw = storage.getItem(SCAN_UPLOAD_STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map(toRecord)
      .filter((record): record is ScanUploadRecord => record !== null)
      .sort((a, b) => (a.uploadedAt < b.uploadedAt ? 1 : a.uploadedAt > b.uploadedAt ? -1 : 0));
  } catch {
    return [];
  }
}

function writeScanUploads(
  records: ScanUploadRecord[],
  storage: ScanUploadStorage | null = defaultScanUploadStorage(),
): void {
  if (!storage) return;
  try {
    storage.setItem(SCAN_UPLOAD_STORAGE_KEY, JSON.stringify(records));
  } catch {
    // localStorage unavailable — the pairing stays in memory for this session.
  }
}

/**
 * Remember one upload. The same capture uploaded twice keeps its newest scan
 * (a re-upload makes a second row on the hub; the page points at the one this
 * device made last), and a scan id can only be claimed by one capture.
 */
export function rememberScanUpload(
  record: ScanUploadRecord,
  storage: ScanUploadStorage | null = defaultScanUploadStorage(),
): void {
  const kept = readScanUploads(storage).filter(
    (existing) => existing.captureId !== record.captureId && existing.scanId !== record.scanId,
  );
  writeScanUploads([record, ...kept], storage);
}

/** Forget the pairing(s) that named this scan — called when it is deleted. */
export function forgetScanUpload(scanId: string, storage: ScanUploadStorage | null = defaultScanUploadStorage()): void {
  writeScanUploads(
    readScanUploads(storage).filter((record) => record.scanId !== scanId),
    storage,
  );
}

/** The hub scan a device capture became, or `null` when it is still local. */
export function uploadedScanIdFor(
  captureId: string,
  storage: ScanUploadStorage | null = defaultScanUploadStorage(),
): string | null {
  return readScanUploads(storage).find((record) => record.captureId === captureId)?.scanId ?? null;
}
